import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  useTimeExtensionsQuery,
  TimeExtension,
  useUpdateTimeExtensionStatus,
  getAllActiveSandbox,
  stopSandboxById,
  useProjectDropdowns,
  ActiveSandboxSession,
  useCourseList,
} from "../api/timeExtensionManagementApi";
import {
  useSendEnhancedMessageMutation,
  SendDirectMessageRequest,
} from "../api/CommunicationApi";
import {
  usePackagesQuery,
  useCreateResourceProfile,
  useResourceProfilesQuery,
  useUpdateResourceProfile,
  useDeleteResourceProfile,
} from "../api/sandboxManagementApi";
import { useQueryClient } from "@tanstack/react-query";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Terminal,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Cpu,
  HardDrive,
  Zap,
  Search,
  MemoryStick,
  DollarSign,
  BarChart3,
  Edit,
  Trash2,
  Circle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { TabNavigation } from "../components/ui/tab-navigation";
import { PageHeader } from "../components/ui/page-header";

const ESSENTIAL_PACKAGES: {
  id: string;
  name: string;
  label?: string;
  category: string;
}[] = [
  { id: "catboost", name: "catboost", category: "Specialized" },
  { id: "lightgbm", name: "lightgbm", category: "Specialized" },
  { id: "matplotlib", name: "matplotlib", category: "Visualization" },
  {
    id: "mysql-connector-python",
    name: "mysql-connector-python",
    label: "mysql-connector-python (MySQL Connector)",
    category: "Database",
  },
  { id: "nltk", name: "nltk", category: "NLP" },
  { id: "numpy", name: "numpy", category: "Core" },
  { id: "pandas", name: "pandas", category: "Core" },
  { id: "plotly", name: "plotly", category: "Visualization" },
  {
    id: "psycopg2-binary",
    name: "psycopg2-binary",
    label: "psycopg2-binary (Postgres Connector)",
    category: "Database",
  },
  { id: "pyspark", name: "pyspark", category: "Big Data" },
  { id: "scikit-learn", name: "scikit-learn", category: "ML/DL" },
  { id: "scipy", name: "scipy", category: "Specialized" },
  { id: "seaborn", name: "seaborn", category: "Visualization" },
  { id: "spacy", name: "spacy", category: "NLP" },
  { id: "sqlalchemy", name: "sqlalchemy", label: "sqlalchemy (ORM)", category: "Database" },
  { id: "statsmodels", name: "statsmodels", category: "Specialized" },
  { id: "xgboost", name: "xgboost", category: "Specialized" },
];

interface SandboxSession {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseName: string;
  assignmentId?: string;
  assignmentTitle?: string;
  status: "active" | "idle" | "terminated" | "suspended";
  startTime: string;
  lastActivity: string;
  timeRemaining: number;
  totalTimeAllocated: number;
  memoryUsage: number;
  cpuUsage: number;
  storageUsage: number;
  environment: "python" | "r" | "jupyter" | "rstudio";
  sessionUrl?: string;
  ipAddress: string;
}

interface ResourceProfile {
  id: string;
  name: string;
  resourceType: "docker" | "uv" | "aws_fargate" | "ec2_vm";
  instanceType: string;
  vCpu: number;
  ramGB: number;
  gpuCount: number;
  diskGB: number;
  idleTimeoutMin: number;
  maxRuntimeHrs: number;
  costBudgetUSD: number;
  tags: string[];
  allowedPackages: string[];
  customPackages: string[];
  active: boolean;
  buildStatus?: string;
  containerImage?: string;
  baseEnvironment?: string;
  fargateTaskDefinition?: string;
  ec2InstanceProfile?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PlatformQuotas {
  maxSandboxesPerUser: number;
  totalvCPUPool: number;
  monthlyCostCap: number;
  currentSandboxes: number;
  currentvCPU: number;
  currentMonthlyCost: number;
}

const mockSandboxSessions: SandboxSession[] = [
  {
    id: "session_001",
    studentId: "student_001",
    studentName: "Rahul Sharma",
    studentEmail: "rahul.sharma@bits.edu",
    courseId: "course_001",
    courseName: "CS F314 - Machine Learning",
    assignmentId: "assign_001",
    assignmentTitle: "Linear Regression Analysis",
    status: "active",
    startTime: "2025-01-12T09:30:00Z",
    lastActivity: "2025-01-12T11:45:00Z",
    timeRemaining: 25,
    totalTimeAllocated: 120,
    memoryUsage: 78,
    cpuUsage: 45,
    storageUsage: 256,
    environment: "jupyter",
    sessionUrl: "https://jupyter-session-001.sandbox.bits.edu",
    ipAddress: "10.0.1.15",
  },
];

const mockPlatformQuotas: PlatformQuotas = {
  maxSandboxesPerUser: 3,
  totalvCPUPool: 100,
  monthlyCostCap: 1000,
  currentSandboxes: 24,
  currentvCPU: 68,
  currentMonthlyCost: 650,
};

function EditResourceProfileModal({
  profile,
  isOpen,
  onClose,
  onSave,
}: {
  profile?: ResourceProfile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (profile: ResourceProfile) => void;
}) {
  const [formData, setFormData] = useState<ResourceProfile>(
    profile || {
      id: "",
      name: "",
      resourceType: "ec2_vm",
      instanceType: "small",
      vCpu: 2,
      ramGB: 4,
      gpuCount: 0,
      diskGB: 20,
      idleTimeoutMin: 30,
      maxRuntimeHrs: 4,
      costBudgetUSD: 2.5,
      tags: [],
      allowedPackages: [],
      customPackages: [],
      active: false,
    }
  );

  const [newPackage, setNewPackage] = useState("");
  const [showCustomPackageInput, setShowCustomPackageInput] = useState(false);
  const [customPackagesString, setCustomPackagesString] = useState("");
  const [cpuInputValue, setCpuInputValue] = useState("");
  const { startResourceProfilePolling } = useAuth();

  // Initialize customPackagesString and filter out custom packages from allowedPackages when editing
  useEffect(() => {
    if (profile) {
      // Set custom packages string and show input if custom packages exist
      if (profile?.customPackages && Array.isArray(profile?.customPackages)) {
        setCustomPackagesString(profile?.customPackages?.join(", "));
        if (profile.customPackages.length > 0) {
          setShowCustomPackageInput(true);
        }
      }

      // Filter out custom packages from allowedPackages to avoid duplication
      if (profile?.allowedPackages && profile?.customPackages) {
        const standardPackages = profile.allowedPackages.filter(
          (pkg) => !profile.customPackages?.includes(pkg)
        );
        setFormData((prev) => ({
          ...prev,
          allowedPackages: standardPackages,
        }));
      }
    }
  }, [profile]);

  // Initialize CPU input value when editing a profile
  useEffect(() => {
    if (profile?.vCpu !== undefined) {
      setCpuInputValue(String(profile.vCpu));
    }
  }, [profile]);

  // Fetch packages from API
  const { data: packagesData, isLoading: packagesLoading } = usePackagesQuery();

  // Success handler for both create and update
  const handleSuccess = (response: any) => {
    const isCreating = !profile;

    // Start polling if build status is PENDING (for both create and update operations)
    if (response.data?.id) {
      toast.success(
        `Your profile ${
          isCreating
            ? "building has been initialized"
            : "update is being processed"
        }...`
      );
      // Start polling at the App level
      startResourceProfilePolling(response.data.id, response.data.name);
    } else {
      toast.success(
        response.message ||
          `Resource profile ${profile ? "updated" : "created"} successfully`
      );
    }

    onClose();

    if (response.data) {
      const savedProfile: ResourceProfile = {
        id: response.data.id,
        name: response.data.name,
        resourceType: response.data.resource_type as any,
        instanceType: "small",
        vCpu: response.data.cpu_cores,
        ramGB: response.data.ram_gb,
        gpuCount: 0,
        diskGB: response.data.disk_gb,
        idleTimeoutMin: response.data.idle_timeout_minutes,
        maxRuntimeHrs: response.data.max_runtime_hours,
        costBudgetUSD: response.data.cost_usd,
        tags: [],
        allowedPackages: response.data.packages,
        customPackages: (() => {
          const customPkgs = response.data.custom_packages;
          if (Array.isArray(customPkgs)) {
            return customPkgs;
          } else if (typeof customPkgs === "string" && customPkgs.trim()) {
            return customPkgs
              .split(",")
              .map((pkg) => pkg.trim())
              .filter((pkg) => pkg);
          } else {
            return [];
          }
        })(),
        active: response.data.is_active,
      };

      onSave(savedProfile);
    }
  };

  // Separate custom packages from selected standard packages
  const getCustomPackagesArray = () => {
    if (!customPackagesString.trim()) return [];
    return customPackagesString
      .split(",")
      .map((pkg) => pkg.trim())
      .filter((pkg) => pkg);
  };

  // Error handler for both create and update
  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.data?.message ||
      error.message ||
      `Failed to ${profile ? "update" : "create"} resource profile`;
    toast.error(errorMessage);
  };

  // Mutation for creating resource profile
  const createProfileMutation = useCreateResourceProfile({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // Mutation for updating resource profile
  const updateProfileMutation = useUpdateResourceProfile({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a profile name");
      return;
    }

    if (formData.vCpu < 0.1) {
      toast.error("CPU cores must be at least 0.1");
      return;
    }

    // Prepare payload for API
    const payload = {
      name: formData.name.trim(),
      resource_type: formData.resourceType,
      cpu_cores: parseFloat(String(formData.vCpu)),
      ram_gb: parseFloat(String(formData.ramGB)),
      disk_gb: parseFloat(String(formData.diskGB)),
      max_runtime_hours: formData.maxRuntimeHrs,
      idle_timeout_minutes: formData.idleTimeoutMin,
      cost_usd: formData.costBudgetUSD,
      packages: formData.allowedPackages, // Array of selected packages
      custom_packages: customPackagesString.trim()
        ? customPackagesString.trim()
        : "", // String of comma-separated custom packages, empty string to clear
      is_active: formData.active,
    };

    // Call appropriate mutation based on whether we're editing or creating
    if (profile?.id) {
      updateProfileMutation.mutate({ id: profile.id, data: payload });
    } else {
      createProfileMutation.mutate(payload);
    }
  };

  const isPending =
    createProfileMutation.isPending || updateProfileMutation.isPending;

  const addCustomPackage = () => {
    if (
      newPackage.trim() &&
      !formData.allowedPackages.includes(newPackage.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        allowedPackages: [...prev.allowedPackages, newPackage.trim()],
      }));
      setNewPackage("");
    }
  };

  const removeCustomPackage = (pkg: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedPackages: prev.allowedPackages.filter((p) => p !== pkg),
    }));
  };

  const togglePackage = (packageName: string) => {
    setFormData((prev) => {
      const isSelected = prev.allowedPackages.includes(packageName);
      return {
        ...prev,
        allowedPackages: isSelected
          ? prev.allowedPackages.filter((p) => p !== packageName)
          : [...prev.allowedPackages, packageName],
      };
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[80vw] min-w-[60vw] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold">
            {profile ? "Edit Resource Profile" : "Create Resource Profile"}
          </DialogTitle>
          <DialogDescription>
            Configure computational resources and constraints for this profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resource Type Selection */}
          <div className="space-y-4">
            {/* <div className="space-y-2">
              <Label className="font-semibold text-base mb-2 text-[16px]">
                Resource Type
              </Label>
              <div className="flex gap-4 w-full">
                <div
                  className={`w-full p-4 bg-white border rounded-xl hover:bg-blue-100/30 cursor-pointer flex gap-x-4 ${
                    formData.resourceType === "docker"
                      ? "border-bits-blue bg-blue-100/30"
                      : "border-gray-200 bg-white"
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, resourceType: "docker" }))
                  }
                >
                  <div className="flex justify-center items-center">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M29.08 13.6671C29 13.6138 28.3333 13.0938 26.8933 13.0938C26.52 13.0938 26.1466 13.1338 25.7733 13.2004C25.4933 11.3338 23.9333 10.3871 23.8666 10.3471L23.48 10.1204L23.24 10.4804C22.92 10.9604 22.6666 11.5071 22.56 12.0671C22.2933 13.1338 22.4533 14.1471 23 15.0138C22.3466 15.3871 21.28 15.4804 21.0533 15.4804H3.49329C3.03996 15.4804 2.66663 15.8538 2.66663 16.3204C2.66663 17.8538 2.90663 19.3871 3.43996 20.8271C4.03996 22.4138 4.94663 23.5871 6.10663 24.3071C7.41329 25.1071 9.55996 25.5604 12 25.5604C13.0533 25.5604 14.1466 25.4671 15.2266 25.2671C16.72 25.0004 18.16 24.4804 19.48 23.7204C20.5732 23.0953 21.5517 22.2881 22.3733 21.3338C23.7733 19.7738 24.6 18.0004 25.2 16.4671H25.4533C26.9733 16.4671 27.92 15.8538 28.44 15.3338C28.7866 15.0138 29.04 14.6271 29.2266 14.1738L29.3333 13.8538L29.08 13.6671ZM5.13329 14.9871H7.47996C7.58663 14.9871 7.69329 14.8938 7.69329 14.7738V12.6671C7.69329 12.5604 7.59996 12.4538 7.47996 12.4538H5.13329C5.01329 12.4538 4.91996 12.5471 4.91996 12.6671V14.7738C4.93329 14.8938 5.01329 14.9871 5.13329 14.9871ZM8.37329 14.9871H10.72C10.8266 14.9871 10.9333 14.8938 10.9333 14.7738V12.6671C10.9333 12.5604 10.84 12.4538 10.72 12.4538H8.37329C8.25329 12.4538 8.15996 12.5471 8.15996 12.6671V14.7738C8.17329 14.8938 8.25329 14.9871 8.37329 14.9871ZM11.6666 14.9871H14C14.1333 14.9871 14.2266 14.8938 14.2266 14.7738V12.6671C14.2266 12.5604 14.1466 12.4538 14 12.4538H11.6666C11.56 12.4538 11.4666 12.5471 11.4666 12.6671V14.7738C11.4666 14.8938 11.5466 14.9871 11.6666 14.9871ZM14.92 14.9871H17.28C17.3866 14.9871 17.48 14.8938 17.48 14.7738V12.6671C17.48 12.5604 17.4 12.4538 17.28 12.4538H14.92C14.8133 12.4538 14.72 12.5471 14.72 12.6671V14.7738C14.72 14.8938 14.8133 14.9871 14.92 14.9871ZM8.37329 12.0004H10.72C10.8266 12.0004 10.9333 11.8804 10.9333 11.7604V9.6671C10.9333 9.5471 10.84 9.45376 10.72 9.45376H8.37329C8.25329 9.45376 8.15996 9.53376 8.15996 9.6671V11.7604C8.17329 11.8804 8.25329 12.0004 8.37329 12.0004ZM11.6666 12.0004H14C14.1333 12.0004 14.2266 11.8804 14.2266 11.7604V9.6671C14.2266 9.5471 14.1466 9.45376 14 9.45376H11.6666C11.56 9.45376 11.4666 9.53376 11.4666 9.6671V11.7604C11.4666 11.8804 11.5466 12.0004 11.6666 12.0004ZM14.92 12.0004H17.28C17.3866 12.0004 17.48 11.8804 17.48 11.7604V9.6671C17.48 9.5471 17.3866 9.45376 17.28 9.45376H14.92C14.8133 9.45376 14.72 9.53376 14.72 9.6671V11.7604C14.72 11.8804 14.8133 12.0004 14.92 12.0004ZM14.92 8.96043H17.28C17.3866 8.96043 17.48 8.8671 17.48 8.7471V6.6671C17.48 6.53376 17.3866 6.44043 17.28 6.44043H14.92C14.8133 6.44043 14.72 6.52043 14.72 6.6671V8.7471C14.72 8.85376 14.8133 8.96043 14.92 8.96043ZM18.2 14.9871H20.5466C20.6666 14.9871 20.76 14.8938 20.76 14.7738V12.6671C20.76 12.5604 20.6666 12.4538 20.5466 12.4538H18.2C18.0933 12.4538 18 12.5471 18 12.6671V14.7738C18 14.8938 18.0933 14.9871 18.2 14.9871Z"
                        fill="#0288D1"
                      />
                    </svg>
                  </div>

                  <button
                    type="button"
                    className={`flex-1 rounded-lg text-left transition-all duration-150  focus:outline-none`}
                  >
                    <div style={{ fontSize: "14px" }} className="font-semibold">
                      Docker Container
                    </div>
                    <div style={{ fontSize: "12px" }} className="text-gray-500">
                      Containerized Environment
                    </div>
                  </button>
                </div>
                <div
                  className={`w-full p-4 border rounded-xl hover:bg-blue-100/30 cursor-pointer flex gap-x-4 ${
                    formData.resourceType === "aws_fargate"
                      ? "border-bits-blue bg-blue-100/30"
                      : "border-gray-200 bg-white"
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      resourceType: "aws_fargate",
                    }))
                  }
                >
                  <div className="flex justify-center items-center">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_3608_37350)">
                        <path
                          d="M0 0H32V32H0V0Z"
                          fill="url(#paint0_linear_3608_37350)"
                        />
                        <path
                          d="M20.8 22.6093V20.6708L22 20.1908V22.1293L20.8 22.6093ZM18.8 20.1908L20 20.6708V22.6093L18.8 22.1293V20.1908ZM16.4 25.4093V23.4708L17.6 22.9908V24.9293L16.4 25.4093ZM14.4 22.9908L15.6 23.4708V25.4093L14.4 24.9293V22.9908ZM12 22.6093V20.6708L13.2 20.1908V22.1293L12 22.6093ZM10 20.1908L11.2 20.6708V22.6093L10 22.1293V20.1908ZM11.6 19.2308L12.5232 19.6L11.6 19.9693L10.6768 19.6L11.6 19.2308ZM16 22.0308L16.9233 22.4L16 22.7693L15.0767 22.4L16 22.0308ZM20.4 19.2308L21.3232 19.6L20.4 19.9693L19.4767 19.6L20.4 19.2308ZM22.5484 19.2284L20.5484 18.4284C20.4532 18.3909 20.3468 18.3909 20.2516 18.4284L18.2516 19.2284C18.1774 19.2582 18.1137 19.3094 18.0689 19.3757C18.024 19.4419 18.0001 19.5201 18 19.6V21.9693L16.1484 21.2284C16.0532 21.1909 15.9468 21.1909 15.8516 21.2284L14 21.9693V19.6C13.9999 19.5201 13.976 19.4419 13.9311 19.3757C13.8863 19.3094 13.8226 19.2582 13.7484 19.2284L11.7484 18.4284C11.6532 18.3909 11.5468 18.3909 11.4516 18.4284L9.45162 19.2284C9.37738 19.2582 9.31373 19.3094 9.26889 19.3757C9.22404 19.4419 9.20005 19.5201 9.2 19.6V22.4C9.2 22.5637 9.29962 22.7104 9.45162 22.7717L11.4516 23.5717C11.4991 23.5904 11.5496 23.6 11.6 23.6C11.6504 23.6 11.7007 23.5904 11.7484 23.5717L13.6 22.8308V25.2C13.6 25.3637 13.6996 25.5104 13.8516 25.5717L15.8516 26.3717C15.8991 26.3904 15.9496 26.4 16 26.4C16.0504 26.4 16.1007 26.3904 16.1484 26.3717L18.1484 25.5717C18.2226 25.5419 18.2863 25.4906 18.3311 25.4244C18.376 25.3582 18.3999 25.28 18.4 25.2V22.8308L20.2516 23.5717C20.2991 23.5904 20.3496 23.6 20.4 23.6C20.4504 23.6 20.5007 23.5904 20.5484 23.5717L22.5484 22.7717C22.6226 22.7419 22.6863 22.6906 22.7311 22.6244C22.776 22.5582 22.7999 22.48 22.8 22.4V19.6C22.7999 19.5201 22.776 19.4419 22.7311 19.3757C22.6863 19.3094 22.6226 19.2582 22.5484 19.2284ZM26 14.3753C26 16.7133 20.8475 17.9753 16 17.9753C11.1525 17.9753 6 16.7133 6 14.3753C6 13.2588 7.238 12.276 9.486 11.6077L9.714 12.3744C7.9165 12.9088 6.8 13.6757 6.8 14.3753C6.8 15.6997 10.578 17.1753 16 17.1753C21.422 17.1753 25.2 15.6997 25.2 14.3753C25.2 13.6757 24.0836 12.9088 22.286 12.3744L22.514 11.6077C24.762 12.276 26 13.2588 26 14.375M16 6.82816L20.0856 8.40003L16 9.97166L11.9144 8.40003L16 6.82816ZM20.232 14.7365C19.4776 15.0742 18.2112 15.4674 16.4 15.5203V10.6747L20.8 8.98266V13.8592C20.8 14.2395 20.5775 14.5838 20.232 14.7365ZM11.2 13.8592V8.98278L15.6 10.6748V15.5204C13.7888 15.4677 12.5225 15.0744 11.7664 14.736C11.4224 14.584 11.2 14.2395 11.2 13.8592ZM11.4416 15.4668C12.322 15.8608 13.8276 16.3308 16 16.3308C18.1725 16.3308 19.678 15.8608 20.5572 15.4677C21.1907 15.1873 21.6 14.556 21.6 13.8593V8.40003C21.6001 8.31914 21.5756 8.24012 21.5298 8.17342C21.484 8.10671 21.4191 8.05546 21.3436 8.02641L16.1436 6.02641C16.0511 5.9912 15.9489 5.9912 15.8564 6.02641L10.6564 8.02641C10.5809 8.05546 10.516 8.10671 10.4702 8.17342C10.4244 8.24012 10.3999 8.31914 10.4 8.40003V13.8593C10.4 14.556 10.8092 15.1873 11.4416 15.4668Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <linearGradient
                          id="paint0_linear_3608_37350"
                          x1="0"
                          y1="3200"
                          x2="3200"
                          y2="0"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#C8511B" />
                          <stop offset="1" stop-color="#FF9900" />
                        </linearGradient>
                        <clipPath id="clip0_3608_37350">
                          <rect width="32" height="32" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <button
                    type="button"
                    className={`flex-1 focus:outline-none rounded-lg text-left transition-all duration-150 ${
                      formData.resourceType === "aws_fargate"
                        ? "border-bits-blue  "
                        : "border-gray-200  "
                    }`}
                  >
                    <div style={{ fontSize: "14px" }} className="font-semibold">
                      AWS Fargate
                    </div>
                    <div style={{ fontSize: "12px" }} className="text-gray-500">
                      Serverless containers
                    </div>
                  </button>
                </div>
                <div
                  className={`w-full p-4 border rounded-xl hover:bg-blue-100/30 cursor-pointer flex gap-x-4 ${
                    formData.resourceType === "ec2_vm"
                      ? "border-bits-blue bg-blue-100/30"
                      : "border-gray-200 bg-white"
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, resourceType: "ec2_vm" }))
                  }
                >
                  <div className="flex justify-center items-center">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_3608_37358)">
                        <path
                          d="M0 0H32V32H0V0Z"
                          fill="url(#paint0_linear_3608_37358)"
                        />
                        <path
                          d="M10.7998 21.2008H20.7998V11.2008H10.7998V21.2008ZM21.5998 11.2008H23.1998V12.0008H21.5998V13.6008H23.1998V14.4008H21.5998V15.6008H23.1998V16.4008H21.5998V18.0008H23.1998V18.8008H21.5998V20.4008H23.1998V21.2008H21.5998V21.2552C21.5996 21.4529 21.521 21.6424 21.3812 21.7822C21.2414 21.922 21.0519 22.0006 20.8542 22.0008H20.7998V23.6008H19.9998V22.0008H18.3998V23.6008H17.5998V22.0008H16.3998V23.6008H15.5998V22.0008H13.9998V23.6008H13.1998V22.0008H11.5998V23.6008H10.7998V22.0008H10.7454C10.5477 22.0006 10.3582 21.922 10.2184 21.7822C10.0786 21.6424 9.99997 21.4529 9.9998 21.2552V21.2008H8.7998V20.4008H9.9998V18.8008H8.7998V18.0008H9.9998V16.4008H8.7998V15.6008H9.9998V14.4008H8.7998V13.6008H9.9998V12.0008H8.7998V11.2008H9.9998V11.1464C9.99997 10.9487 10.0786 10.7591 10.2184 10.6194C10.3582 10.4796 10.5477 10.4009 10.7454 10.4008H10.7998V8.80078H11.5998V10.4008H13.1998V8.80078H13.9998V10.4008H15.5998V8.80078H16.3998V10.4008H17.5998V8.80078H18.3998V10.4008H19.9998V8.80078H20.7998V10.4008H20.8542C21.0519 10.4009 21.2414 10.4796 21.3812 10.6194C21.521 10.7591 21.5996 10.9487 21.5998 11.1464V11.2008ZM16.3998 26.3512C16.3997 26.3643 16.3944 26.3769 16.3852 26.3861C16.3759 26.3954 16.3633 26.4007 16.3502 26.4008H5.64943C5.6363 26.4007 5.62373 26.3954 5.61445 26.3861C5.60516 26.3769 5.5999 26.3643 5.5998 26.3512V15.6504C5.5999 15.6373 5.60516 15.6247 5.61445 15.6154C5.62373 15.6061 5.6363 15.6009 5.64943 15.6008H7.9998V14.8008H5.64943C5.42418 14.801 5.20822 14.8906 5.04895 15.0499C4.88967 15.2092 4.80007 15.4252 4.7998 15.6504V26.3512C4.80007 26.5764 4.88967 26.7924 5.04895 26.9516C5.20822 27.1109 5.42418 27.2005 5.64943 27.2008H16.3502C16.5754 27.2005 16.7914 27.1109 16.9507 26.9516C17.1099 26.7924 17.1995 26.5764 17.1998 26.3512V24.4008H16.3998V26.3512ZM27.1998 5.65041V16.3512C27.1995 16.5764 27.1099 16.7924 26.9507 16.9516C26.7914 17.1109 26.5754 17.2005 26.3502 17.2008H23.9998V16.4008H26.3502C26.3633 16.4007 26.3759 16.3954 26.3852 16.3861C26.3944 16.3769 26.3997 16.3643 26.3998 16.3512V5.65041C26.3997 5.63727 26.3944 5.62471 26.3852 5.61542C26.3759 5.60614 26.3633 5.60088 26.3502 5.60078H15.6494C15.6363 5.60088 15.6237 5.60614 15.6144 5.61542C15.6052 5.62471 15.5999 5.63727 15.5998 5.65041V8.00078H14.7998V5.65041C14.8001 5.42515 14.8897 5.2092 15.0489 5.04992C15.2082 4.89064 15.4242 4.80105 15.6494 4.80078H26.3502C26.5754 4.80105 26.7914 4.89064 26.9507 5.04992C27.1099 5.2092 27.1995 5.42515 27.1998 5.65041Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <linearGradient
                          id="paint0_linear_3608_37358"
                          x1="0"
                          y1="3200"
                          x2="3200"
                          y2="0"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#C8511B" />
                          <stop offset="1" stop-color="#FF9900" />
                        </linearGradient>
                        <clipPath id="clip0_3608_37358">
                          <rect width="32" height="32" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <button
                    type="button"
                    className={`flex-1 focus:outline-none  rounded-lg text-left transition-all duration-150 ${
                      formData.resourceType === "ec2_vm"
                        ? "border-bits-blue  "
                        : "border-gray-200 "
                    }`}
                  >
                    <div style={{ fontSize: "14px" }} className="font-semibold">
                      EC2 Virtual Machine
                    </div>
                    <div style={{ fontSize: "12px" }} className="text-gray-500">
                      Full Virtual Machine
                    </div>
                  </button>
                </div>
              </div>
            </div> */}
            <div></div>
            <div className="text-[16px] font-semibold">
              <Label className="text-[16px] font-semibold">
                Basic Information
              </Label>
            </div>
            <div className="flex gap-x-4 w-full">
              <div className="space-y-2 w-full">
                <Label htmlFor="profileName" className="text-[14px]">
                  Profile Name *
                </Label>
                <Input
                  id="profileName"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., High CPU VM"
                  className="w-full"
                />
              </div>
              <div className="space-y-2 w-full">
                <Label htmlFor="diskGB" className="text-[14px]">
                  {" "}
                  Storage (GB) *
                </Label>
                <Input
                  id="diskGB"
                  type="text"
                  value={formData.diskGB}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow numbers and one decimal point
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setFormData((prev) => ({
                        ...prev,
                        diskGB: value === "" ? 0 : parseFloat(value),
                      }));
                    }
                  }}
                  placeholder="e.g., 50 or 50.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cpuCores" className="text-[14px]">
                  CPU Cores *
                </Label>
                <Input
                  id="cpuCores"
                  type="text"
                  value={cpuInputValue}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty string
                    if (value === "") {
                      setCpuInputValue("");
                      setFormData((prev) => ({
                        ...prev,
                        vCpu: 0,
                      }));
                      return;
                    }

                    // Allow numbers and one decimal point
                    if (/^\d*\.?\d*$/.test(value)) {
                      setCpuInputValue(value);
                      const numValue = parseFloat(value);
                      setFormData((prev) => ({
                        ...prev,
                        vCpu: isNaN(numValue) ? 0 : numValue,
                      }));
                    }
                  }}
                  placeholder="e.g., 2 or 2.5"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ramGB" className="text-[14px]">
                  RAM (GB) *
                </Label>
                <Input
                  id="ramGB"
                  type="text"
                  value={formData.ramGB}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow numbers and one decimal point
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setFormData((prev) => ({
                        ...prev,
                        ramGB: value === "" ? 0 : parseFloat(value),
                      }));
                    }
                  }}
                  placeholder="e.g., 8 or 8.5"
                />
              </div>
            </div>
            {/* <div className="text-[16px] font-semibold">
<Label className="text-[16px] font-semibold">Limits</Label>
</div>


            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="maxRuntime" className="text-[14px]">Max Runtime (hours) *</Label>
                <Input
                  id="maxRuntime"
                  type="text"
                  value={formData.maxRuntimeHrs}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '' || parseInt(value) >= 1) {
                      setFormData((prev) => ({
                        ...prev,
                        maxRuntimeHrs: value === '' ? 0 : parseInt(value),
                      }));
                    }
                  }}
                  placeholder="e.g., 4"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="idleTimeout" className="text-[14px]">Idle Timeout (minutes) *</Label>
                <Input
                  id="idleTimeout"
                  type="text"
                  value={formData.idleTimeoutMin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '' || parseInt(value) >= 5) {
                      setFormData((prev) => ({
                        ...prev,
                        idleTimeoutMin: value === '' ? 0 : parseInt(value),
                      }));
                    }
                  }}
                  placeholder="e.g., 30"
                />
              </div>
            </div>
 */}
            {/* <div className="space-y-2">
              <Label htmlFor="costUSD">Cost (USD) *</Label>
              <Input
                id="costUSD"
                type="text"
                value={formData.costBudgetUSD}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = value.split('.');
                  if (parts.length <= 2) {
                    const validValue = parts.length === 2 
                      ? `${parts[0]}.${parts[1].slice(0, 2)}`
                      : value;
                    if (validValue === '' || parseFloat(validValue) >= 0.1) {
                      setFormData((prev) => ({
                        ...prev,
                        costBudgetUSD: validValue === '' ? 0 : parseFloat(validValue),
                      }));
                    }
                  }
                }}
                placeholder="e.g., 2.5"
              />
            </div> */}

            <div className="space-y-2">
              <h4 className="font-medium mb-4">Packages</h4>

              <div className="space-y-3">
                {packagesLoading ? (
                  <div className="text-sm text-gray-500">
                    Loading packages...
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Available Packages:
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto border rounded-md p-3">
                      {/* Essential Data Science Packages */}
                      {ESSENTIAL_PACKAGES.map((pkg) => (
                        <div
                          key={pkg.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`pkg-${pkg.id}`}
                            checked={formData.allowedPackages.includes(
                              pkg.name
                            )}
                            onCheckedChange={() => togglePackage(pkg.name)}
                          />
                          <label
                            htmlFor={`pkg-${pkg.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {pkg.label ?? pkg.name}
                            {/* <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">
                              {pkg.category}
                            </Badge> */}
                          </label>
                        </div>
                      ))}

                      {/* API Packages (exclude duplicates with essential packages) */}
                      {packagesData?.packages
                        ?.filter(
                          (pkg: any) =>
                            !ESSENTIAL_PACKAGES.some(
                              (essentialPkg) =>
                                essentialPkg.name.toLowerCase() ===
                                pkg.name.toLowerCase()
                            )
                        )
                        .sort((a: any, b: any) =>
                          a.name
                            .toLowerCase()
                            .localeCompare(b.name.toLowerCase())
                        )
                        .map((pkg: any) => (
                          <div
                            key={pkg.id}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`pkg-${pkg.id}`}
                              checked={formData.allowedPackages.includes(
                                pkg.name
                              )}
                              onCheckedChange={() => togglePackage(pkg.name)}
                            />
                            <label
                              htmlFor={`pkg-${pkg.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {pkg.name}
                            </label>
                          </div>
                        ))}
                    </div>

                    {/* Add/Edit Custom Packages Button */}
                    <div className="mt-3">
                      <Button
                        type="button"
                        onClick={() =>
                          setShowCustomPackageInput(!showCustomPackageInput)
                        }
                        className="w-full bg-white hover:bg-gray-50 text-bits-blue border border-bits-blue"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {customPackagesString.trim()
                          ? "Edit Custom Packages"
                          : "Add Custom Packages"}
                      </Button>
                    </div>

                    {/* Custom Package Input Field */}
                    {showCustomPackageInput && (
                      <div className="space-y-2 mt-3 p-3 border rounded-md bg-gray-50">
                        <Label className="text-sm font-medium">
                          Custom Packages (comma-separated)
                        </Label>
                        <Input
                          value={customPackagesString}
                          onChange={(e) =>
                            setCustomPackagesString(e.target.value)
                          }
                          placeholder="Add the custom packages you want separated by comma"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-600">
                          Enter package names separated by commas. They will be
                          included in your resource profile automatically.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Package Input */}
                {/* <div className="space-y-2 pt-2">
                  <Label className="text-sm font-medium">
                    Or add custom package:
                  </Label>
                  <div className="flex gap-2 items-stretch">
                    <Input
                      value={newPackage}
                      onChange={(e) => setNewPackage(e.target.value)}
                      placeholder="Enter package name (e.g., pandas, numpy)"
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), addCustomPackage())
                      }
                    />
                    <Button 
                      type="button" 
                      onClick={addCustomPackage} 
                      className="bg-bits-blue hover:bg-bits-blue/90 text-white whitespace-nowrap h-auto px-4"
                    >
                      <Plus className="h-4 w-4 mr-1 text-white" />
                      Add
                    </Button>
                  </div>
                </div> */}

                {formData.allowedPackages.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm font-medium">
                      Selected Packages ({formData.allowedPackages.length}):
                    </Label>
                    <div className="flex flex-wrap gap-1">
                      {formData.allowedPackages
                        .sort((a, b) =>
                          a.toLowerCase().localeCompare(b.toLowerCase())
                        )
                        .map((pkg) => (
                          <Badge
                            key={pkg}
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            {pkg}
                            <button
                              type="button"
                              onClick={() => removeCustomPackage(pkg)}
                              className="text-red-500 hover:text-red-700 ml-1"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-4 border-t mt-6">
              <Switch
                id="active-toggle"
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, active: checked }))
                }
              />
              <Label htmlFor="active-toggle" className="text-sm font-medium">Activate Profile</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isPending}
            className="bg-bits-blue hover:bg-bits-blue/90"
          >
            {isPending
              ? profile
                ? "Updating..."
                : "Creating..."
              : profile
              ? "Update Profile"
              : "Create Profile"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SandboxManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [profilesPage, setProfilesPage] = useState(1);
  const [profilesLimit, setProfilesLimit] = useState(10);
  const [sessions, setSessions] = useState<SandboxSession[]>([]);
  const [sandboxFilter, setSandboxFilter] = useState({
    selectedProject: "",
    selectedCourse: "",
    selectedStudent: "",
    currentPage: 1,
    itemsPerPage: 10,
  });

  // Filter states for resource profiles - store as strings to allow typing decimals
  const [cpuCoresFilter, setCpuCoresFilter] = useState<string>("");
  const [ramGbFilter, setRamGbFilter] = useState<string>("");
  const [diskGbFilter, setDiskGbFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<
    "created_at" | "updated_at" | "name" | "cpu_cores" | "ram_gb"
  >("created_at");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Get tab from URL params, default to "sessions"
  const mode = searchParams.get("mode") || "sessions";
  const action = searchParams.get("action");

  // Fetch resource profiles from API with pagination and filters
  const {
    data: resourceProfilesData,
    isLoading: profilesLoading,
    error: profilesError,
    refetch: refetchResourceProfiles,
  } = useResourceProfilesQuery({
    page: profilesPage,
    limit: profilesLimit,
    cpu_cores: cpuCoresFilter ? parseFloat(cpuCoresFilter) : undefined,
    ram_gb: ramGbFilter ? parseFloat(ramGbFilter) : undefined,
    disk_gb: diskGbFilter ? parseFloat(diskGbFilter) : undefined,
    sortBy,
    sortOrder,
  });

  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    "pending"
  );

  const { data, isLoading, error } = useTimeExtensionsQuery({
    page,
    limit,
    status: statusFilter as any,
  });
  const {
    data: sandboxList,
    isLoading: isSandboxListLoading,
    error: isSandboxListError,
  } = getAllActiveSandbox(sandboxFilter);
  const {
    data: dropdownValue,
    isLoading: isDropdownLoading,
    error: isDropdownError,
  } = useProjectDropdowns(sandboxFilter);
  const { data: courseList, isLoading: isCoursesLoading } = useCourseList();
  const sendMessageMutation = useSendEnhancedMessageMutation();

  const sendTimeExtensionDecisionMessage = async (params: {
    recipientId: string;
    studentName: string;
    projectName?: string;
    status: "approve" | "reject";
    durationValue?: string;
    reviewerName?: string;
    reviewComments?: string | null;
  }) => {
    const {
      recipientId,
      studentName,
      projectName,
      status,
      durationValue,
      reviewerName,
      reviewComments,
    } = params;
    let messageContent;
    if (status === "approve") {
      messageContent = ` Sandbox extension ${"approved"}

  Hi ${studentName},
   Duration: ${durationValue} | Project: ${
        selectedRequest?.sandboxSession?.project?.title || "N/A"
      } | Approved by ${reviewerName} on ${new Date().toLocaleString("en-IN")}.
  Instructor comment: ${reviewComments}`;
    } else {
      messageContent = ` Sandbox extension ${"rejected"}

    Instructor comment: ${reviewComments}`;
    }

    const payload: SendDirectMessageRequest = {
      recipientId,
      subject: `Time extension ${
        status === "approve" ? "approved" : "rejected"
      }`,
      content: messageContent,
    };
    await sendMessageMutation.mutateAsync(payload);
  };

  const updateStatusMutation = useUpdateTimeExtensionStatus({
    onSuccess: () => {
      toast.success("Time extension request updated successfully");

      // Refetch the time extensions query to update the list
      queryClient.invalidateQueries({ queryKey: ["timeExtensions"] });
    },
    onError: (error) => {
      const errorMessage =
        (error as any)?.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      toast.error(`Failed to update request: ${errorMessage}`);
    },
  });
  const FILTER_ALL = "all";

  // Fallback mock data for testing UI
  const mockData: {
    rows: TimeExtension[];
    count: number;
    page: number;
    totalPages: number;
  } = {
    rows: [
      {
        id: "test-1",
        sandbox_session_id: "session-1",
        student_id: "student-1",
        reason: "System lag caused timeout",
        duration_value: 2,
        duration_unit: "hours",
        status: "pending",
        reviewed_by: null,
        reviewed_at: "2025-10-17T10:01:22.874Z",
        review_comments: null,
        approved_due_date: null,
        created_at: "2025-10-17T10:01:22.875Z",
        updated_at: "2025-10-17T10:01:22.875Z",
        createdAt: "2025-10-17T10:01:22.875Z",
        updatedAt: "2025-10-17T10:01:22.875Z",
        deletedAt: null,
        sandboxSession_id: "session-1",
        student: {
          id: "student-1",
          name: "Test Student",
          email: "test@example.com",
        },
        sandboxSession: {
          id: "session-1",
          user_id: "student-1",
          project_id: "project-1",
          startTime: "2025-10-17T09:59:23.303Z",
          endTime: "2025-10-17T10:01:47.765Z",
          duration: 144,
          lastHeartbeat: "2025-10-17T10:01:31.703Z",
          status: "closed",
          extension_requested: true,
          createdAt: "2025-10-17T09:59:23.303Z",
          updatedAt: "2025-10-17T10:01:47.766Z",
          project: {
            id: "project-1",
            title: "Test Project",
            course_id: "course-1",
          },
        },
      },
    ],
    count: 1,
    page: 1,
    totalPages: 1,
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [platformQuotas, setPlatformQuotas] = useState(mockPlatformQuotas);

  // Modal state derived from URL params
  const showCreateModal = action === "create-profile";
  const profileIdToEdit = action?.startsWith("edit-profile-")
    ? action.replace("edit-profile-", "")
    : null;

  // Convert ResourceProfileData to ResourceProfile for editing
  const editingProfile = React.useMemo(() => {
    if (!profileIdToEdit || !resourceProfilesData?.profiles) return null;

    const apiProfile = resourceProfilesData.profiles.find(
      (p) => p?.id === profileIdToEdit
    );
    if (!apiProfile) return null;

    return {
      id: apiProfile?.id || "",
      name: apiProfile?.name || "",
      resourceType: (apiProfile?.resource_type as any) || "ec2_vm",
      instanceType: "ec2_vm" as const,
      vCpu: apiProfile?.cpu_cores || 0,
      ramGB: apiProfile?.ram_gb || 0,
      gpuCount: 0,
      diskGB: apiProfile?.disk_gb || 0,
      idleTimeoutMin: apiProfile?.idle_timeout_minutes || 0,
      maxRuntimeHrs: apiProfile?.max_runtime_hours || 0,
      costBudgetUSD: apiProfile?.cost_usd || 0,
      tags: [],
      allowedPackages: apiProfile?.packages || [],
      customPackages: (() => {
        const customPkgs = apiProfile?.custom_packages;
        if (Array.isArray(customPkgs)) {
          return customPkgs;
        } else if (typeof customPkgs === "string" && customPkgs.trim()) {
          return customPkgs
            .split(",")
            .map((pkg) => pkg.trim())
            .filter((pkg) => pkg);
        } else {
          return [];
        }
      })(),
      active: apiProfile?.is_active ?? false,
    };
  }, [profileIdToEdit, resourceProfilesData]);

  // Review dialog state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(
    null
  );
  const [reviewRequestId, setReviewRequestId] = useState<string | null>(null);
  const [reviewComments, setReviewComments] = useState("");
  const [customDurationHours, setCustomDurationHours] = useState("");
  const [customDurationMinutes, setCustomDurationMinutes] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<TimeExtension | null>(
    null
  );

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);

  const handleSaveProfile = (profile: ResourceProfile) => {
    // Refetch resource profiles after creation/update
    queryClient.invalidateQueries({ queryKey: ["resourceProfiles"] });
  };

  // Mutation for toggling profile status
  const toggleProfileMutation = useUpdateResourceProfile({
    onSuccess: () => {
      toast.success("Profile status updated successfully");
      refetchResourceProfiles();
      queryClient.invalidateQueries({ queryKey: ["resourceProfiles"] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.data?.message ||
        error.message ||
        "Failed to update profile status";
      toast.error(errorMessage);
    },
  });
  let userLogin = localStorage.getItem("auth_user");
  let userDetail = userLogin ? JSON.parse(userLogin) : null;
  const toggleProfileStatus = (profileId: string) => {
    // Find the profile from the API data
    const profileData = resourceProfilesData?.profiles?.find(
      (p) => p?.id === profileId
    );

    if (!profileData) {
      toast.error("Profile not found");
      return;
    }

    // Toggle the is_active status
    const updatedPayload = {
      id: profileId,
      data: {
        name: profileData?.name || "",
        resource_type: profileData?.resource_type || "ec2_vm",
        cpu_cores: profileData?.cpu_cores || 0,
        ram_gb: profileData?.ram_gb || 0,
        disk_gb: Number(profileData?.disk_gb) || 0,
        max_runtime_hours: profileData?.max_runtime_hours || 0,
        idle_timeout_minutes: profileData?.idle_timeout_minutes || 0,
        cost_usd: profileData?.cost_usd || 0,
        packages: profileData?.packages || [],
        is_active: !(profileData?.is_active ?? false), // Toggle the status
      },
    };

    toggleProfileMutation.mutate(updatedPayload);
  };

  // Mutation for deleting profile
  const deleteProfileMutation = useDeleteResourceProfile({
    onSuccess: () => {
      toast.success("Profile deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["resourceProfiles"] });
      setShowDeleteDialog(false);
      setDeleteProfileId(null);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.data?.message ||
        error.message ||
        "Failed to delete profile";
      setShowDeleteDialog(false);
      setDeleteProfileId(null);

      toast.error(errorMessage);
    },
  });

  const handleDeleteProfile = (profileId: string) => {
    setDeleteProfileId(profileId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteProfile = () => {
    if (deleteProfileId) {
      deleteProfileMutation.mutate(deleteProfileId);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteProfileId(null);
  };

  const terminateSession = async (sessionId: ActiveSandboxSession) => {
    // setSessions((prev) =>
    //   prev.map((s) => (s.id === sessionId ? { ...s, status: "terminated" } : s))
    // );
    try {
      const body = {
        user_id: sessionId.user.id,
        session_id: sessionId.id,
      };
      await stopSandboxById(body);
      toast.success("Session terminated successfully");
      queryClient.invalidateQueries({ queryKey: ["activeSandbox"] });
    } catch (err: any) {
      toast.error(err?.message || "Unable to stop sandbox");
    }
  };

  const extendSession = (sessionId: string, additionalMinutes: number) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId
          ? { ...s, timeRemaining: s.timeRemaining + additionalMinutes }
          : s
      )
    );
    toast.success(`Session extended by ${additionalMinutes} minutes`);
  };

  const handleRequest = (
    requestId: string,
    action: "approve" | "reject",
    request: any
  ) => {
    setReviewRequestId(requestId);
    setReviewAction(action);
    setSelectedRequest(request);
    setReviewComments("");
    setShowReviewDialog(true);
  };

  const submitReview = async () => {
    if (!reviewRequestId || !reviewAction || !selectedRequest) return;

    const status = reviewAction === "approve" ? "approved" : "rejected";
    const review_comments = reviewComments.trim() || `Request ${reviewAction}d`;
    const reviewed_by = user?.id || "admin"; // Replace with actual admin user ID from context/auth

    // Format duration as "HHH:MM"
    const hours = customDurationHours || "0";
    const minutes = customDurationMinutes || "0";
    if (hours === "0" && minutes === "0") {
      toast.success("Please enter valid duration to approve");
      return;
    }
    const duration_value = `${hours.padStart(3, "0")}:${minutes.padStart(
      2,
      "0"
    )}`;

    updateStatusMutation.mutate({
      id: reviewRequestId,
      params: {
        status,
        review_comments,
        reviewed_by,
        duration_value,
      },
    });

    await sendTimeExtensionDecisionMessage({
      recipientId: selectedRequest.student.id,
      studentName: selectedRequest.student.name,
      projectName: selectedRequest.sandboxSession?.project?.title || "N/A",
      status: reviewAction,
      durationValue: duration_value,
      ...(user?.name && { reviewerName: user.name }),
      reviewComments,
    });

    // Close dialog and reset state
    setSelectedRequest(null);
    setShowReviewDialog(false);
    setReviewRequestId(null);
    setReviewAction(null);
    setReviewComments("");
    setCustomDurationHours("");
    setCustomDurationMinutes("");
  };

  const closeReviewDialog = () => {
    setShowReviewDialog(false);
    setReviewRequestId(null);
    setReviewAction(null);
    setReviewComments("");
    setCustomDurationHours("");
    setCustomDurationMinutes("");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "timed_out":
        return "text-yellow-600 bg-yellow-100";
      case "closed":
        return "text-red-600 bg-red-100";
      case "suspended":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getDurationColor = (unit: string) => {
    switch (unit) {
      case "hours":
        return "text-blue-600 bg-blue-100";
      case "days":
        return "text-purple-600 bg-purple-100";
      case "minutes":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // const filteredSessions = sessions.filter(
  //   (session) =>
  //     session.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     session.courseName.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  // Convert API data to ResourceProfile format with safe optional chaining
  const resourceProfiles: ResourceProfile[] =
    resourceProfilesData?.profiles?.map((profile) => ({
      id: profile?.id || "",
      name: profile?.name || "Unnamed Profile",
      resourceType: (profile?.resource_type as any) || "ec2_vm",
      instanceType: "ec2_vm", // Not in API, using default
      vCpu: profile?.cpu_cores || 0,
      ramGB: profile?.ram_gb || 0,
      gpuCount: 0, // Not in API
      diskGB: profile?.disk_gb || 0,
      idleTimeoutMin: profile?.idle_timeout_minutes || 0,
      maxRuntimeHrs: profile?.max_runtime_hours || 0,
      costBudgetUSD: 0, // Not in API
      tags: [], // Not in API
      allowedPackages: profile?.packages || [],
      customPackages: (() => {
        const customPkgs = profile?.custom_packages;
        if (Array.isArray(customPkgs)) {
          return customPkgs;
        } else if (typeof customPkgs === "string" && customPkgs.trim()) {
          return customPkgs
            .split(",")
            .map((pkg) => pkg.trim())
            .filter((pkg) => pkg);
        } else {
          return [];
        }
      })(),
      active: profile?.is_active,
      ...(profile?.build_status ? { buildStatus: profile.build_status } : {}),
      createdAt: profile?.createdAt,
      updatedAt: profile?.updatedAt,
    })) || [];

  // No need for client-side filtering since we're using API filters
  const filteredProfiles = resourceProfiles;

  const pagination = resourceProfilesData?.pagination;

  function formatSecondsToHMS(
    seconds: string | number,
    returnSecond?: boolean
  ) {
    // Handle string input like "005:15" or "5:15"
    if (typeof seconds === "string") {
      const trimmed = seconds.trim();
      // Match formats: "H:MM", "HH:MM", "HHH:MM", etc.
      const match = trimmed.match(/^(\d+):(\d{1,2})$/);
      if (match) {
        const hours = parseInt(match[1]!, 10) || 0;
        const minutes = parseInt(match[2]!, 10) || 0;
        seconds = hours * 3600 + minutes * 60;
      } else {
        // If unrecognized string, try parsing as number
        seconds = parseFloat(seconds) || 0;
      }
    }
    if (returnSecond) return seconds;
    // Ensure non-negative & integer seconds
    const totalSec = Math.max(0, Math.floor(Math.abs(seconds)));

    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;

    return [
      String(h).padStart(2, "0"),
      String(m).padStart(2, "0"),
      String(s).padStart(2, "0"),
    ].join(":");
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Sandbox Management"
        subtitle="Monitor and manage sandbox environments, resource allocation, and student requests"
      />

      {/* Tab Navigation */}
      <TabNavigation
        tabs={[
          {
            key: "sessions",
            label: "Active Sessions",
            icon: <Terminal className="h-4 w-4" />,
          },
          {
            key: "profiles",
            label: "Resource Profiles",
            icon: <Cpu className="h-4 w-4" />,
          },
          {
            key: "requests",
            label: "Time Extension Requests",
            icon: <Clock className="h-4 w-4" />,
          },
        ]}
        activeTab={mode}
        onTabChange={(tab) => setSearchParams({ mode: tab })}
        className="mb-6"
      />

      {/* Sessions Tab */}
      {mode === "sessions" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Active Sandbox Sessions
            </CardTitle>
            <CardDescription>
              Monitor and manage currently running sandbox environments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4 ">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search sessions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-4">
                  <Select
                    value={sandboxFilter?.selectedCourse}
                    onValueChange={(e) =>
                      setSandboxFilter((prev) => ({
                        ...prev,
                        selectedCourse: e,
                        selectedProject: "all",
                        selectedStudent: "all",
                      }))
                    }
                  >
                    <SelectTrigger className="w-[200px] cursor-pointer">
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent
                      style={{ maxHeight: "240px", overflowY: "auto" }}
                      className="max-h-[300px] overflow-y-auto"
                    >
                      <SelectItem value="all">All Courses</SelectItem>
                      {isCoursesLoading ? (
                        <SelectItem value="loading">
                          Loading courses...
                        </SelectItem>
                      ) : (
                        courseList?.courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate block max-w-[160px]">
                                  {course.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{course.label}</p>
                              </TooltipContent>
                            </Tooltip>
                            {/* {course.label} */}
                          </SelectItem>
                        ))
                      )}
                      {/* {uniqueCourses.map((course) => (
                    <TooltipProvider key={course.id} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <SelectItem value={course.id} className="cursor-pointer">
                              <span className="truncate block">{course.name}</span>
                            </SelectItem>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[400px] z-[100]">
                          <p className="text-sm">{course.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))} */}
                    </SelectContent>
                  </Select>
                  <Select
                    value={sandboxFilter?.selectedProject || "all"}
                    onValueChange={(e) =>
                      setSandboxFilter((prev) => ({
                        ...prev,
                        selectedProject: e,
                      }))
                    }
                  >
                    <SelectTrigger className="w-[200px] cursor-pointer">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent
                      style={{ maxHeight: "240px", overflowY: "auto" }}
                      className="max-h-[300px] overflow-y-auto"
                    >
                      <SelectItem value="all">All Projects</SelectItem>
                      {isDropdownLoading ? (
                        <SelectItem value="loading">
                          Loading Projects
                        </SelectItem>
                      ) : userDetail.role === "admin" &&
                        !sandboxFilter.selectedCourse ? (
                        <SelectItem value="loading" disabled>
                          Please Select Course
                        </SelectItem>
                      ) : (
                        dropdownValue?.projects?.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate block max-w-[160px]">
                                  {category.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{category.label}</p>
                              </TooltipContent>
                            </Tooltip>
                            {/* {category.label} */}
                          </SelectItem>
                        ))
                      )}
                      {/* {uniqueCourses.map((course) => (
                    <TooltipProvider key={course.id} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <SelectItem value={course.id} className="cursor-pointer">
                              <span className="truncate block">{course.name}</span>
                            </SelectItem>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[400px] z-[100]">
                          <p className="text-sm">{course.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))} */}
                    </SelectContent>
                  </Select>

                  <Select
                    value={sandboxFilter?.selectedStudent}
                    onValueChange={(e) =>
                      setSandboxFilter((prev) => ({
                        ...prev,
                        selectedStudent: e,
                      }))
                    }
                  >
                    <SelectTrigger className="w-[200px] cursor-pointer">
                      <SelectValue placeholder="All Students" />
                    </SelectTrigger>
                    <SelectContent
                      style={{ maxHeight: "240px", overflowY: "auto" }}
                      className="max-h-[300px] overflow-y-auto"
                    >
                      <SelectItem value="all">All Students</SelectItem>
                      {isDropdownLoading ? (
                        <SelectItem value="loading">
                          Loading Students
                        </SelectItem>
                      ) : userDetail.role === "admin" &&
                        !sandboxFilter.selectedCourse ? (
                        <SelectItem value="loading" disabled>
                          Please Select Course
                        </SelectItem>
                      ) : (
                        dropdownValue?.students?.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate block max-w-[160px]">
                                  {category.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{category.label}</p>
                              </TooltipContent>
                            </Tooltip>
                            {/* {category.label} */}
                          </SelectItem>
                        ))
                      )}
                      {/* {uniqueCourses.map((course) => (
                    <TooltipProvider key={course.id} delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <SelectItem value={course.id} className="cursor-pointer">
                              <span className="truncate block">{course.name}</span>
                            </SelectItem>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[400px] z-[100]">
                          <p className="text-sm">{course.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))} */}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Course/Assignment</TableHead>
                      {/* <TableHead>Environment</TableHead> */}
                      <TableHead>Status</TableHead>
                      <TableHead>Time Remaining</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isSandboxListLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <div className="text-center space-y-2">Loading</div>
                        </TableCell>
                      </TableRow>
                    ) : isSandboxListError ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <div className="text-center space-y-2">
                            Unable to Load Active List
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : sandboxList?.activeList?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <div className="text-center space-y-2">
                            No Data Found
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sandboxList &&
                      sandboxList?.activeList?.map((session) => {
                        // const elapsedSec = formatSecondsToHMS(
                        //   session.totalTime,
                        //   true
                        // ); // 1h15m = 4500 sec
                        const progress = Math.min(
                          100,
                          Number(session.totalTime) > 0
                            ? (Number(session.totalUsedTime) /
                                Number(session.totalTime)) *
                                100
                            : 0
                        );
                        return (
                          <TableRow key={session.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {session.user?.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {session.user?.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {session.projectAndCourseDetail?.course?.code}{" "}
                                  -{" "}
                                  {session.projectAndCourseDetail?.course?.name}
                                </div>
                                {session.projectAndCourseDetail?.title && (
                                  <div className="text-sm text-gray-500">
                                    {session.projectAndCourseDetail?.title}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            {/* <TableCell>
                          <Badge variant="outline">{session.environment}</Badge>
                        </TableCell> */}
                            <TableCell>
                              <Badge className={getStatusColor(session.status)}>
                                {session.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {formatSecondsToHMS(
                                  session.totalUsedTime,
                                  false
                                )}{" "}
                                / {formatSecondsToHMS(session.totalTime, false)}
                              </div>
                              <Progress
                                value={
                                  progress
                                  // (session.timeRemaining /
                                  //   session.duration) *
                                  // 100
                                }
                                className="h-2 mt-1"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {/* {session.status === "active" && ( */}
                                <>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => terminateSession(session)}
                                    className="bg-bits-red"
                                  >
                                    Terminate
                                  </Button>
                                </>
                                {/* )} */}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div>
              {sandboxList && sandboxList.pagination?.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-bits-grey-600">
                    Page {sandboxList?.pagination.currentPage} of{" "}
                    {sandboxList?.pagination.totalPages} •{" "}
                    {sandboxList?.pagination.totalItems} items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        sandboxFilter.currentPage <= 1 || isSandboxListLoading
                      }
                      onClick={() =>
                        setSandboxFilter((prev) => ({
                          ...prev,
                          currentPage: Math.max(1, prev.currentPage - 1),
                        }))
                      }
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={
                        sandboxFilter.currentPage >=
                          sandboxList.pagination?.totalPages ||
                        isSandboxListLoading
                      }
                      onClick={() =>
                        setSandboxFilter((prev) => ({
                          ...prev,
                          currentPage: prev.currentPage + 1,
                        }))
                      }
                    >
                      Next
                    </Button>
                    <Select
                      value={String(sandboxFilter.itemsPerPage)}
                      onValueChange={(v) => {
                        setSandboxFilter((prev) => ({
                          ...prev,
                          currentPage: 1,
                          itemsPerPage: Number(v),
                        }));
                        // setLimit(Number(v));
                        // setPage(1);
                      }}
                    >
                      <SelectTrigger className="w-28 bg-bits-grey-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 20, 30, 50].map((l) => (
                          <SelectItem key={l} value={String(l)}>
                            {l} / page
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource Profiles Tab */}
      {mode === "profiles" && (
        <div className="space-y-6">
          {/* Resource Profiles */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="h-5 w-5" />
                    Resource Profiles
                  </CardTitle>
                  <CardDescription>
                    Configure computational resource templates for sandbox
                    environments
                  </CardDescription>
                </div>
                <Button
                  onClick={() =>
                    setSearchParams({
                      mode: "profiles",
                      action: "create-profile",
                    })
                  }
                  className="bg-bits-blue hover:bg-bits-blue/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Profile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Filter Controls */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {/* CPU Cores Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="cpu-filter" className="text-sm font-medium">
                      Min CPU Cores
                    </Label>
                    <Input
                      id="cpu-filter"
                      type="text"
                      value={cpuCoresFilter}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow numbers and one decimal point
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setCpuCoresFilter(value);
                          setProfilesPage(1);
                        }
                      }}
                      placeholder="e.g., 2 or 2.5"
                      className="w-full"
                    />
                  </div>

                  {/* RAM Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="ram-filter" className="text-sm font-medium">
                      Min RAM (GB)
                    </Label>
                    <Input
                      id="ram-filter"
                      type="text"
                      value={ramGbFilter}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow numbers and one decimal point
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setRamGbFilter(value);
                          setProfilesPage(1);
                        }
                      }}
                      placeholder="e.g., 8 or 8.5"
                      className="w-full"
                    />
                  </div>

                  {/* Disk Filter */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="disk-filter"
                      className="text-sm font-medium"
                    >
                      Min Disk (GB)
                    </Label>
                    <Input
                      id="disk-filter"
                      type="text"
                      value={diskGbFilter}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow numbers and one decimal point
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setDiskGbFilter(value);
                          setProfilesPage(1);
                        }
                      }}
                      placeholder="e.g., 50 or 50.5"
                      className="w-full"
                    />
                  </div>

                  {/* Sort By */}
                  <div className="space-y-2">
                    <Label htmlFor="sort-by" className="text-sm font-medium">
                      Sort By
                    </Label>
                    <Select
                      value={sortBy}
                      onValueChange={(value: any) => {
                        setSortBy(value);
                        setProfilesPage(1);
                      }}
                    >
                      <SelectTrigger id="sort-by" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at">Created Date</SelectItem>
                        <SelectItem value="updated_at">Updated Date</SelectItem>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="cpu_cores">CPU Cores</SelectItem>
                        <SelectItem value="ram_gb">RAM (GB)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sort Order and Clear Filters */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="sort-order" className="text-sm font-medium">
                      Order:
                    </Label>
                    <Select
                      value={sortOrder}
                      onValueChange={(value: any) => {
                        setSortOrder(value);
                        setProfilesPage(1);
                      }}
                    >
                      <SelectTrigger id="sort-order" className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ASC">Ascending</SelectItem>
                        <SelectItem value="DESC">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clear Filters Button */}
                  {(cpuCoresFilter || ramGbFilter || diskGbFilter) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCpuCoresFilter("");
                        setRamGbFilter("");
                        setDiskGbFilter("");
                        setProfilesPage(1);
                      }}
                      className="ml-auto"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>

                {/* Search input hidden for now */}
                {/* <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search profiles..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div> */}

                {profilesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bits-blue mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading profiles...</p>
                    </div>
                  </div>
                ) : profilesError ? (
                  <div className="text-center py-12">
                    <Cpu className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-red-600 mb-2">Error loading profiles</p>

                    <p className="text-sm text-gray-500">
                      {(profilesError as any)?.response?.data?.data?.message ||
                        "Unknown error occurred"}
                    </p>
                  </div>
                ) : filteredProfiles.length === 0 ? (
                  <div className="text-center py-12">
                    <Cpu className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No resource profiles found</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {cpuCoresFilter || ramGbFilter || diskGbFilter
                        ? "Try adjusting your filters or create a new profile"
                        : "Create your first profile to get started"}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredProfiles.map((profile) => (
                      <Card
                        key={profile.id}
                        className={`border-l-4 ${
                          profile.active
                            ? "border-l-green-500 bg-green-50/30"
                            : "border-l-gray-400 bg-gray-50/30"
                        }`}
                      >
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="font-medium">
                                  {profile?.name || "Unnamed Profile"}
                                </h3>
                                <Badge variant="outline">
                                  {profile?.resourceType?.toUpperCase() ||
                                    "EC2_VM"}
                                </Badge>
                                {/* <Badge variant="outline">
                                {profile.instanceType}
                              </Badge> */}
                                {!profile?.active && (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                                {/* Build Status Badge */}
                                {profile?.buildStatus && (
                                  <Badge
                                    variant={
                                      profile.buildStatus === "COMPLETED"
                                        ? "default"
                                        : profile.buildStatus === "FAILED"
                                        ? "destructive"
                                        : "secondary"
                                    }
                                    className={`flex items-center gap-1 ${
                                      profile.buildStatus === "COMPLETED"
                                        ? "bg-green-100 text-green-800 border-green-300"
                                        : profile.buildStatus === "FAILED"
                                        ? "bg-red-100 text-red-800 border-red-300"
                                        : profile.buildStatus === "PENDING"
                                        ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                        : "bg-gray-100 text-gray-800 border-gray-300"
                                    }`}
                                  >
                                    {profile.buildStatus === "COMPLETED" && (
                                      <CheckCircle className="h-3 w-3" />
                                    )}
                                    {profile.buildStatus === "FAILED" && (
                                      <XCircle className="h-3 w-3" />
                                    )}
                                    {profile.buildStatus === "PENDING" && (
                                      <Circle className="h-3 w-3 animate-spin" />
                                    )}
                                    {![
                                      "COMPLETED",
                                      "FAILED",
                                      "PENDING",
                                    ].includes(profile.buildStatus) && (
                                      <AlertCircle className="h-3 w-3" />
                                    )}
                                    {profile.buildStatus}
                                  </Badge>
                                )}
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                  <Cpu className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm">
                                    {profile?.vCpu || 0} vCPU
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MemoryStick className="h-4 w-4 text-green-600" />
                                  <span className="text-sm">
                                    {profile?.ramGB || 0}GB RAM
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <HardDrive className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm">
                                    {profile?.diskGB || 0}GB Disk
                                  </span>
                                </div>
                                {/* <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-purple-600" />
                                <span className="text-sm">
                                   check${profile.costBudgetUSD}
                                </span>
                              </div> */}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-600" />
                                  <span className="text-sm">
                                    Max: {profile?.maxRuntimeHrs || 0}h, Idle:{" "}
                                    {profile?.idleTimeoutMin || 0}m
                                  </span>
                                </div> */}
                                {(profile?.gpuCount || 0) > 0 && (
                                  <div className="flex items-center gap-2">
                                    <Zap className="h-4 w-4 text-yellow-600" />
                                    <span className="text-sm">
                                      {profile?.gpuCount || 0} GPU
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Created and Updated timestamps */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-xs text-gray-500">
                                {/* {profile?.createdAt && (
                                <div>
                                  <span className="font-medium">Created: </span>
                                  {new Date(profile.createdAt).toLocaleString()}
                                </div>
                              )} */}
                                {profile?.updatedAt && (
                                  <div>
                                    <span className="font-medium">
                                      Updated:{" "}
                                    </span>
                                    {new Date(
                                      profile.updatedAt
                                    ).toLocaleString()}
                                  </div>
                                )}
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    Packages:{" "}
                                  </span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {profile?.allowedPackages
                                      ?.slice(0, 5)
                                      ?.map((pkg) => (
                                        <Badge
                                          key={pkg}
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {pkg}
                                        </Badge>
                                      ))}
                                    {(profile?.allowedPackages?.length || 0) >
                                      5 && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span>
                                            <Badge
                                              variant="outline"
                                              className="text-xs cursor-pointer"
                                            >
                                              +
                                              {(profile?.allowedPackages
                                                ?.length || 0) - 5}{" "}
                                              more
                                            </Badge>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          className="z-[9999] bg-gray-900 text-white border-gray-700"
                                        >
                                          <div className="max-w-xs">
                                            <p className="font-medium mb-1 text-white">
                                              Additional packages:
                                            </p>
                                            <p className="text-xs text-gray-200">
                                              {profile?.allowedPackages
                                                ?.slice(5)
                                                .join(", ")}
                                            </p>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>

                                {profile?.customPackages &&
                                  profile?.customPackages?.length > 0 && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">
                                        Custom Packages:{" "}
                                      </span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {profile?.customPackages
                                          ?.slice(0, 3)
                                          ?.map((pkg) => (
                                            <Badge
                                              key={pkg}
                                              variant="secondary"
                                              className="text-xs bg-blue-100 text-blue-800"
                                            >
                                              {pkg}
                                            </Badge>
                                          ))}
                                        {(profile?.customPackages?.length ||
                                          0) > 3 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                <Badge
                                                  variant="secondary"
                                                  className="text-xs bg-blue-100 text-blue-800 cursor-pointer"
                                                >
                                                  +
                                                  {(profile?.customPackages
                                                    ?.length || 0) - 3}{" "}
                                                  more
                                                </Badge>
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="z-[9999] bg-gray-900 text-white border-gray-700"
                                            >
                                              <div className="max-w-xs">
                                                <p className="font-medium mb-1 text-white">
                                                  Additional custom packages:
                                                </p>
                                                <p className="text-xs text-gray-200">
                                                  {profile?.customPackages
                                                    ?.slice(3)
                                                    .join(", ")}
                                                </p>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setSearchParams({
                                    mode: "profiles",
                                    action: `edit-profile-${profile?.id || ""}`,
                                  })
                                }
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleDeleteProfile(profile?.id || "")
                                }
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {/* <Switch
                              checked={profile?.active ?? false}
                              onCheckedChange={() =>
                                toggleProfileStatus(profile?.id || '')
                              }
                              className={!profile?.active ? "data-[state=unchecked]:bg-gray-300" : ""}
                            /> */}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination for Resource Profiles */}
                {pagination && pagination.totalItems > 0 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {((pagination.currentPage || profilesPage) - 1) *
                          profilesLimit +
                          1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          (pagination.currentPage || profilesPage) *
                            profilesLimit,
                          pagination.totalItems || 0
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {pagination.totalItems || 0}
                      </span>{" "}
                      profiles
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProfilesPage((p) => Math.max(1, p - 1))
                        }
                        disabled={profilesPage === 1}
                      >
                        Previous
                      </Button>

                      <div className="flex items-center gap-1">
                        {pagination.totalPages &&
                          Array.from(
                            { length: pagination.totalPages },
                            (_, i) => i + 1
                          )
                            .filter((p) => {
                              return (
                                p === 1 ||
                                p === pagination.totalPages ||
                                Math.abs(p - profilesPage) <= 1
                              );
                            })
                            .map((p, i, arr) => (
                              <React.Fragment key={p}>
                                {i > 0 && arr[i - 1] !== p - 1 && (
                                  <span className="px-2 text-gray-400">
                                    ...
                                  </span>
                                )}
                                <Button
                                  variant={
                                    p === profilesPage ? "default" : "outline"
                                  }
                                  size="sm"
                                  onClick={() => setProfilesPage(p)}
                                  className={
                                    p === profilesPage
                                      ? "bg-bits-blue hover:bg-blue-700"
                                      : ""
                                  }
                                >
                                  {p}
                                </Button>
                              </React.Fragment>
                            ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setProfilesPage((p) => p + 1)}
                        disabled={profilesPage >= (pagination.totalPages || 1)}
                      >
                        Next
                      </Button>

                      <Select
                        value={String(profilesLimit)}
                        onValueChange={(v) => {
                          setProfilesLimit(Number(v));
                          setProfilesPage(1);
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 10, 20, 50].map((val) => (
                            <SelectItem key={val} value={String(val)}>
                              {val} / page
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Time Extension Requests Tab */}
      {mode === "requests" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Time Extension Requests
            </CardTitle>
            <CardDescription>
              Review and approve student requests for additional sandbox time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Filter and Search */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter Dropdown */}
                <Select
                  value={statusFilter || "all"}
                  onValueChange={(v) => {
                    setStatusFilter(v === "all" ? undefined : v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40 bg-bits-grey-100">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Requests</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bits-blue mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading requests...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="text-center py-8">
                  <p className="text-red-600">
                    Error loading requests:{" "}
                    {error?.message || "Please try again."}
                  </p>
                </div>
              )}

              {/* Requests List */}
              {!isLoading && (
                <>
                  {(() => {
                    const displayData = data?.data || (error ? mockData : null);
                    if (
                      !displayData ||
                      !displayData?.rows ||
                      displayData?.rows?.length === 0
                    ) {
                      return (
                        <div className="text-center py-8">
                          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">
                            {error
                              ? "Error loading requests - showing test data below"
                              : "No requests found"}
                          </p>
                          {error && (
                            <p className="text-red-500 text-sm mt-2">
                              Error: {error?.message || "Unknown error"}
                            </p>
                          )}
                        </div>
                      );
                    }
                    return displayData?.rows?.map((request) => (
                      <Card
                        key={request?.id || Math.random()}
                        className="border-l-4 border-l-blue-500"
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h4 className="font-medium">
                                  {request?.student?.name || "Unknown Student"}
                                </h4>
                                <Badge
                                  className={getDurationColor(
                                    request?.duration_unit || "hours"
                                  )}
                                >
                                  {request?.duration_value || 0}{" "}
                                  {request?.duration_unit || "hours"}
                                </Badge>
                                <Badge
                                  variant={
                                    request?.status === "pending"
                                      ? "outline"
                                      : request?.status === "approved"
                                      ? "default"
                                      : "destructive"
                                  }
                                >
                                  {request?.status || "unknown"}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                  <span className="text-sm font-medium">
                                    Student Email:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    {request?.student?.email || "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium">
                                    Project:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    {request?.sandboxSession?.project?.title ||
                                      "N/A"}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium">
                                    Session Duration:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    {request?.sandboxSession?.duration
                                      ? Math.round(
                                          request.sandboxSession.duration / 60
                                        )
                                      : 0}{" "}
                                    minutes
                                  </div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium">
                                    Session Status:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {request?.sandboxSession?.status ||
                                        "unknown"}
                                    </Badge>
                                  </div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium">
                                    Requested Extension:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    {request?.duration_value || 0}{" "}
                                    {request?.duration_unit || "hours"}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-sm font-medium">
                                    Request Date:
                                  </span>
                                  <div className="text-sm text-gray-600">
                                    {request?.created_at
                                      ? new Date(
                                          request.created_at
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>

                              <div className="mb-4">
                                <span className="text-sm font-medium">
                                  Reason:
                                </span>
                                <div className="text-sm text-gray-600 mt-1 p-3 bg-gray-50 rounded-md">
                                  {request?.reason || "No reason provided"}
                                </div>
                              </div>

                              {/* Show session details */}
                              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                                <div className="text-sm">
                                  <span className="font-medium">
                                    Session Details:
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                                  <div>
                                    <span className="font-medium">Start: </span>
                                    {request?.sandboxSession?.startTime
                                      ? new Date(
                                          request.sandboxSession.startTime
                                        ).toLocaleString()
                                      : "N/A"}
                                  </div>
                                  <div>
                                    <span className="font-medium">End: </span>
                                    {request?.sandboxSession?.endTime
                                      ? new Date(
                                          request.sandboxSession.endTime
                                        ).toLocaleString()
                                      : "N/A"}
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Last Activity:{" "}
                                    </span>
                                    {request?.sandboxSession?.lastHeartbeat
                                      ? new Date(
                                          request.sandboxSession.lastHeartbeat
                                        ).toLocaleString()
                                      : "N/A"}
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Extension Requested:{" "}
                                    </span>
                                    {request?.sandboxSession
                                      ?.extension_requested
                                      ? "Yes"
                                      : "No"}
                                  </div>
                                </div>
                              </div>

                              {/* Show review details if approved/rejected */}
                              {(request?.status === "approved" ||
                                request?.status === "rejected") && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      Reviewed by:{" "}
                                    </span>
                                    {request?.reviewer?.name || "N/A"}
                                  </div>
                                  {request?.reviewed_at && (
                                    <div className="text-sm text-gray-600">
                                      {new Date(
                                        request.reviewed_at
                                      ).toLocaleDateString()}
                                    </div>
                                  )}
                                  {request?.review_comments && (
                                    <div className="text-sm text-gray-600 mt-2">
                                      <span className="font-medium">
                                        Comments:{" "}
                                      </span>
                                      {request.review_comments}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {request?.status === "pending" && (
                              <div className="flex items-center gap-2 ml-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={updateStatusMutation.isPending}
                                  onClick={() =>
                                    handleRequest(
                                      request?.id || "",
                                      "approve",
                                      request
                                    )
                                  }
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  {updateStatusMutation.isPending
                                    ? "Processing..."
                                    : "Approve"}
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="bg-bits-red"
                                  disabled={updateStatusMutation.isPending}
                                  onClick={() =>
                                    handleRequest(
                                      request?.id || "",
                                      "reject",
                                      request
                                    )
                                  }
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  {updateStatusMutation.isPending
                                    ? "Processing..."
                                    : "Reject"}
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ));
                  })()}

                  {/* Pagination */}
                  {(() => {
                    const displayData = data?.data || (error ? mockData : null);
                    return (
                      displayData &&
                      (displayData?.count || 0) > 0 && (
                        <div className="flex items-center justify-between mt-6">
                          <div className="text-sm text-gray-700">
                            Showing{" "}
                            <span className="font-medium">
                              {((displayData?.page || page) - 1) * limit + 1}
                            </span>{" "}
                            to{" "}
                            <span className="font-medium">
                              {Math.min(
                                (displayData?.page || page) * limit,
                                displayData?.count || 0
                              )}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">
                              {displayData?.count || 0}
                            </span>{" "}
                            requests
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={page === 1}
                            >
                              Previous
                            </Button>

                            <div className="flex items-center gap-1">
                              {displayData?.totalPages &&
                                Array.from(
                                  { length: displayData.totalPages },
                                  (_, i) => i + 1
                                )
                                  .filter((p) => {
                                    return (
                                      p === 1 ||
                                      p === displayData.totalPages ||
                                      Math.abs(p - page) <= 1
                                    );
                                  })
                                  .map((p, i, arr) => (
                                    <React.Fragment key={p}>
                                      {i > 0 && arr[i - 1] !== p - 1 && (
                                        <span className="px-2">...</span>
                                      )}
                                      <Button
                                        variant={
                                          p === page ? "default" : "outline"
                                        }
                                        size="sm"
                                        onClick={() => setPage(p)}
                                        className={
                                          p === page ? "bg-bits-blue" : ""
                                        }
                                      >
                                        {p}
                                      </Button>
                                    </React.Fragment>
                                  ))}
                            </div>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setPage((p) =>
                                  Math.min(displayData?.totalPages || 1, p + 1)
                                )
                              }
                              disabled={page === (displayData?.totalPages || 1)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )
                    );
                  })()}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={closeReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === "approve" ? "Approve" : "Reject"} Time Extension
              Request
            </DialogTitle>
            <DialogDescription>
              Please provide your review comments for this time extension
              request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="review-comments">
                Review Comments {reviewAction === "reject" ? "*" : "(Optional)"}
              </Label>
              <Textarea
                id="review-comments"
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder={
                  reviewAction === "approve"
                    ? "Enter reason for approval (optional)..."
                    : "Please provide reason for rejection..."
                }
                rows={4}
                className="resize-none"
              />
            </div>

            {reviewAction === "approve" && (
              <div className="space-y-2 mt-4">
                <Label>Duration</Label>
                <div className="flex gap-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="custom-hours" className="text-sm">
                      Hours
                    </Label>
                    <Input
                      id="custom-hours"
                      type="text"
                      value={customDurationHours}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d+$/.test(value)) {
                          setCustomDurationHours(value);
                        }
                      }}
                      placeholder="Enter hours"
                      className="w-full"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="custom-minutes" className="text-sm">
                      Minutes
                    </Label>
                    <Input
                      id="custom-minutes"
                      type="text"
                      value={customDurationMinutes}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (/^\d+$/.test(value) && parseInt(value) <= 59)
                        ) {
                          setCustomDurationMinutes(value);
                        }
                      }}
                      placeholder="Enter minutes"
                      maxLength={2}
                      className="w-full"
                    />
                  </div>
                </div>
                {/* <p className="text-xs text-gray-500">
                  Leave empty to use the student's requested duration
                </p> */}
              </div>
            )}

            {/* Validation Error Message */}
            {reviewAction === "approve" &&
              (customDurationHours || customDurationMinutes) &&
              parseInt(customDurationHours || "0") === 0 &&
              parseInt(customDurationMinutes || "0") === 0 && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
                  <p className="font-medium">⚠️ Validation Error</p>
                  <p>
                    Custom duration must be greater than 0. Please enter valid
                    hours and/or minutes.
                  </p>
                  <p className="text-xs mt-1">
                    Current duration would be: 000:00 (invalid)
                  </p>
                </div>
              )}

            {reviewAction === "approve" && (
              <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-md">
                <p className="font-medium text-green-800">Approving Request</p>
                <p>This will grant the student the requested time extension.</p>
              </div>
            )}

            {reviewAction === "reject" && (
              <div className="text-sm text-gray-600 bg-red-50 p-3 rounded-md">
                <p className="font-medium text-red-800">Rejecting Request</p>
                <p>
                  This will deny the time extension request. Please provide a
                  clear reason.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeReviewDialog}>
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={
                updateStatusMutation.isPending ||
                (reviewAction === "reject" && !reviewComments.trim()) ||
                (reviewAction === "approve" &&
                  (customDurationHours.trim() !== "" ||
                    customDurationMinutes.trim() !== "") &&
                  parseInt(customDurationHours || "0") === 0 &&
                  parseInt(customDurationMinutes || "0") === 0)
              }
              className={
                reviewAction === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {updateStatusMutation.isPending
                ? "Processing..."
                : reviewAction === "approve"
                ? "Approve Request"
                : "Reject Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Profile Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={closeDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Resource Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this resource profile? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-gray-600 bg-red-50 p-4 rounded-md border border-red-200">
              <p className="font-medium text-red-800 mb-2">⚠️ Warning</p>
              <ul className="list-disc list-inside space-y-1 text-red-700">
                <li>This will permanently delete the resource profile</li>
                <li>Any active sessions using this profile may be affected</li>
                <li>This action cannot be reversed</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deleteProfileMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteProfile}
              disabled={deleteProfileMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteProfileMutation.isPending
                ? "Deleting..."
                : "Delete Profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Profile Modal */}
      {(showCreateModal || editingProfile !== null) && (
        <EditResourceProfileModal
          isOpen={true}
          onClose={() => setSearchParams({ mode: "profiles" })}
          {...(editingProfile && { profile: editingProfile })}
          onSave={handleSaveProfile}
        />
      )}
    </div>
  );
}
